import { Component, OnInit, OnDestroy } from '@angular/core';
import { ProductService, Product, SelectedProduct, normalizeSalePrice } from '../../services/products';
import { CustomerSelectionService } from '../../services/customer-selection';
import { getPrice, PricelistService } from '../../services/price-lists';
import { CartService } from '../../services/cart';
import { MessageDialogService } from '../../services/message-dialog';
import { CanComponentDeactivate } from '../../guards/cart-prompt-guard';
import { Customer } from '../../services/customer';
import { CommonModule } from '@angular/common';
import { Observable, Subject, takeUntil, of } from 'rxjs';
import { ProductListComponent } from '../../components/product-list-component/product-list-component';
import { MatButtonModule } from '@angular/material/button';
import { CustomerFavoritesService } from '../../services/customer-favorites-service';
import { ProductUpdateService } from '../../services/product-update.service';

@Component({
  selector: 'app-order',
  standalone: true,
  templateUrl: './order-component.html',
  styleUrls: ['./order-component.scss'],
  imports: [
    CommonModule,
    ProductListComponent,
    MatButtonModule
  ]
})
export class OrderComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  selectedCustomer: Customer | null = null;
  selectedProducts: SelectedProduct[] = [];
  products: Product[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private productService: ProductService,
    private customerService: CustomerSelectionService,
    private pricelistService: PricelistService,
    private cartService: CartService,
    private messageDialogService: MessageDialogService,
    private favoritesService: CustomerFavoritesService,
    private productUpdateService: ProductUpdateService
  ) { }

  ngOnInit(): void {
    let cachedCart: SelectedProduct[] = [];

    this.cartService.cartChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(cart => cachedCart = cart);

    this.productService.get().subscribe(products => {
      this.customerService.selection$
        .pipe(takeUntil(this.destroy$))
        .subscribe(customer => {
          this.selectedCustomer = customer;

          // Load last prices cache and apply to products; fall back to pricelist if missing
          this.pricelistService.get().subscribe(pricelists => {
            const matching = pricelists.find(p => p.id === customer?.priceListId);

            const customerId = customer?.id;
            if (customerId) {
              this.favoritesService.getCachedMap(customerId).subscribe(lastPriceMap => {
                this.products = products.map(product => {
                  const last = lastPriceMap.get(product.id);
                  const priceFromLast = last?.last_price;
                  const fallback = getPrice(matching, product);
                  return { ...product, list_price: normalizeSalePrice(product, priceFromLast ?? fallback) };
                });

                if (cachedCart.length) {
                  this.selectedProducts = cachedCart;
                }
              });
            } else {
              this.products = products.map(product => ({ ...product, list_price: normalizeSalePrice(product, getPrice(matching, product)) }));
              if (cachedCart.length) {
                this.selectedProducts = cachedCart;
              }
            }
          });
        });
    });

    // Listen for product updates from sync operations
    this.productUpdateService.productUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshProducts();
      });
  }

  private refreshProducts(): void {
    // Refresh products with current customer selection
    this.productService.get(true).subscribe(products => {
      this.customerService.selection$
        .pipe(takeUntil(this.destroy$))
        .subscribe(customer => {
          this.selectedCustomer = customer;

          // Load last prices cache and apply to products; fall back to pricelist if missing
          this.pricelistService.get().subscribe(pricelists => {
            const matching = pricelists.find(p => p.id === customer?.priceListId);

            const customerId = customer?.id;
            if (customerId) {
              this.favoritesService.getCachedMap(customerId).subscribe(lastPriceMap => {
                this.products = products.map(product => {
                  const last = lastPriceMap.get(product.id);
                  const priceFromLast = last?.last_price;
                  const fallback = getPrice(matching, product);
                  return { ...product, list_price: normalizeSalePrice(product, priceFromLast ?? fallback) };
                });
              });
            } else {
              this.products = products.map(product => ({ ...product, list_price: normalizeSalePrice(product, getPrice(matching, product)) }));
            }
          });
        });
    });
  }

  async onCartUpdated(selectedProducts: SelectedProduct[]) {
    await this.cartService.updateCart(selectedProducts);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  canDeactivate(): Observable<boolean> {
    if (this.selectedProducts.length === 0) return of(true);
    return this.messageDialogService.confirm(
      'You have items in your cart. Leave this page?',
      'Confirm Navigation'
    );
  }
}
